import { CONFIG } from '../config.js';

const localeMap = {
  'en': 'en-GB', // Use en-GB for dates (DD/MM/YYYY format)
  'fr': 'fr-FR',
  'de': 'de-DE',
  'lb': 'de-DE', // Luxembourgish uses German formatting
  'it': 'it-IT',
  'es': 'es-ES',
  'pt': 'pt-PT'
};

/**
 * Get locale from cookie or browser (same approach as CONFIG.getLocaleFromClient)
 * Used as default for date formatting functions
 * 
 * @returns {string} Locale code (cookie > browser > default)
 */
function getLocaleForFormatting() {
  // Use CONFIG.getLocaleFromClient() which handles cookie > browser > default
  if (typeof CONFIG !== 'undefined' && typeof CONFIG.getLocaleFromClient === 'function') {
    return CONFIG.getLocaleFromClient();
  }
  // Fallback if CONFIG not available (shouldn't happen in normal usage)
  return 'fr';
}

/**
 * Internal helper to parse and validate a date input
 * @param {string|Date} dateInput - The date to parse
 * @returns {Date|null} Parsed Date object or null if invalid
 */
function parseAndValidateDate(dateInput) {
  if (!dateInput) return null;
  
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  
  if (isNaN(date.getTime())) {
    return null;
  }
  
  return date;
}

/**
 * Format date with locale-aware formatting
 * 
 * @param {string|Date} dateInput - The date to format (ISO string, Date object, or null/undefined)
 * @param {string} locale - Optional locale code (e.g., 'en', 'fr', 'de'). If not provided, uses 'fr'
 * @param {object} options - Optional formatting options
 * @param {string} options.style - Date style: 'short', 'medium', 'long', 'full' (default: 'long')
 * @param {string} options.timeZone - Timezone (default: undefined, uses local timezone)
 * @returns {string} Formatted date string or '-' if date is invalid
 */
export function formatDate(dateInput, locale = null, options = {}) {
  // Use cookie/browser locale if not provided
  if (!locale) {
    locale = getLocaleForFormatting();
  }
  
  const date = parseAndValidateDate(dateInput);
  if (!date) {
    return '-';
  }
  
  // Locale already determined from cookie > browser > default via getLocaleForFormatting()
  const dateLocale = localeMap[locale] || 'fr-FR';
  
  // Default options
  const defaultOptions = {
    style: 'long', // long format: "25 December 2024"
    ...options
  };
  
  // Map style to Intl options
  const styleMap = {
    'short': { year: 'numeric', month: 'numeric', day: 'numeric' },
    'medium': { year: 'numeric', month: 'short', day: 'numeric' },
    'long': { year: 'numeric', month: 'long', day: 'numeric' },
    'full': { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }
  };
  
  const formatOptions = {
    ...styleMap[defaultOptions.style] || styleMap.long,
    ...(defaultOptions.timeZone && { timeZone: defaultOptions.timeZone })
  };
  
  return date.toLocaleDateString(dateLocale, formatOptions);
}

/**
 * Format date and time with locale-aware formatting
 * 
 * @param {string|Date} dateInput - The date to format (ISO string, Date object, or null/undefined)
 * @param {string} locale - Optional locale code (e.g., 'en', 'fr', 'de'). If not provided, uses 'fr'
 * @param {object} options - Optional formatting options
 * @param {string} options.dateStyle - Date style: 'short', 'medium', 'long', 'full' (default: 'medium')
 * @param {string} options.timeStyle - Time style: 'short', 'medium', 'long' (default: 'short')
 * @returns {string} Formatted date and time string or '-' if date is invalid
 */
export function formatDateTime(dateInput, locale = null, options = {}) {
  // Use cookie/browser locale if not provided
  if (!locale) {
    locale = getLocaleForFormatting();
  }
  // Handle null, undefined, or empty values
  if (!dateInput) {
    return '-';
  }
  
  // Convert to Date object if it's a string
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '-';
  }
  
  // Locale already determined from cookie > browser > default via getLocaleForFormatting()
  const dateLocale = localeMap[locale] || 'fr-FR';
  
  // Default options
  const defaultOptions = {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...options
  };
  
  // Use Intl.DateTimeFormat for better control
  return new Intl.DateTimeFormat(dateLocale, {
    dateStyle: defaultOptions.dateStyle,
    timeStyle: defaultOptions.timeStyle
  }).format(date);
}

/**
 * Format date in short format (numeric: DD/MM/YYYY or MM/DD/YYYY depending on locale)
 * 
 * @param {string|Date} dateInput - The date to format
 * @param {string} locale - Optional locale code
 * @returns {string} Formatted date string or '-' if date is invalid
 */
export function formatDateShort(dateInput, locale = null) {
  // Use cookie/browser locale if not provided
  if (!locale) {
    locale = getLocaleForFormatting();
  }
  return formatDate(dateInput, locale, { style: 'short' });
}

/**
 * Format date in medium format (e.g., "25 Dec 2024")
 * 
 * @param {string|Date} dateInput - The date to format
 * @param {string} locale - Optional locale code
 * @returns {string} Formatted date string or '-' if date is invalid
 */
export function formatDateMedium(dateInput, locale = null) {
  // Use cookie/browser locale if not provided
  if (!locale) {
    locale = getLocaleForFormatting();
  }
  return formatDate(dateInput, locale, { style: 'medium' });
}

/**
 * Format date as "Month Year" (e.g., "January 2025")
 * Uses UTC to avoid timezone issues
 * 
 * @param {string|Date} dateInput - The date to format
 * @param {string} locale - Optional locale code
 * @returns {string} Formatted date string or '-' if invalid
 */
export function formatMonthYear(dateInput, locale = null) {
  // Use cookie/browser locale if not provided
  if (!locale) {
    locale = getLocaleForFormatting();
  }
  if (!dateInput) return '-';
  
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return '-';
  
  const dateLocale = localeMap[locale] || 'fr-FR';
  
  // Use UTC timezone to avoid timezone shifts
  return new Intl.DateTimeFormat(dateLocale, {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC'
  }).format(date);
}

/**
 * Localized full month names (January…December) for the given locale.
 * Same Intl style as formatMonthYear / formatContractDate.
 *
 * @param {string} [locale]
 * @returns {string[]} length-12 array, index 0 = January
 */
export function getLocalizedMonthNames(locale = null) {
  if (!locale) {
    locale = getLocaleForFormatting();
  }
  const dateLocale = localeMap[locale] || 'fr-FR';
  return Array.from({ length: 12 }, (_, i) => {
    const date = new Date(Date.UTC(2000, i, 1));
    return new Intl.DateTimeFormat(dateLocale, {
      month: 'long',
      timeZone: 'UTC'
    }).format(date);
  });
}

/**
 * Check if a date is a custom date (not 1st or last day of month)
 * Uses UTC to avoid timezone issues
 * Handles leap years correctly
 * 
 * @param {string|Date} dateInput - The date to check
 * @returns {boolean} True if date is not 1st or last day of month
 */
export function isCustomDate(dateInput) {
  if (!dateInput) return false;
  
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return false;
  
  // Use UTC date to avoid timezone shifts
  const day = date.getUTCDate();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();
  
  // Check if it's the first day of the month
  if (day === 1) {
    return false;
  }
  
  // Calculate the last day of the month
  // new Date(year, month + 1, 0) gives the last day of the current month
  // This automatically handles leap years (February 29 in leap years, 28 otherwise)
  const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  
  // Check if it's the last day of the month
  if (day === lastDayOfMonth) {
    return false;
  }
  
  // It's a custom date (neither first nor last day)
  return true;
}

/**
 * Get today's date as a string in YYYY-MM-DD format
 * Uses local date components to avoid timezone issues
 * 
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getTodayDateString() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format contract date: shows full date if custom (not 1st), otherwise month/year
 * Uses UTC timezone to avoid timezone shifts (contract dates are stored in UTC)
 * 
 * @param {string|Date} dateInput - The date to format
 * @param {string} locale - Optional locale code
 * @returns {string} Formatted date string or '-' if invalid
 */
export function formatContractDate(dateInput, locale = null) {
  //console.log('formatContractDate', dateInput, locale);
  // Use cookie/browser locale if not provided
  if (!locale) {
    locale = getLocaleForFormatting();
  }
  
  const date = parseAndValidateDate(dateInput);
  if (!date) {
    return '-';
  }
  
  const dateLocale = localeMap[locale] || 'fr-FR';
  
  // Format using UTC to avoid timezone shifts (contract dates are stored in UTC)
  // If it's a custom date (not 1st of month), show full date
  if (isCustomDate(dateInput)) {
    return date.toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC' // Explicitly use UTC for contract dates
    });
  }
  
  // Otherwise, show month/year only (also in UTC)
  return date.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC' // Explicitly use UTC for contract dates
  });
}

/**
 * Get CSS class for contract dates based on their proximity to today
 * Compares dates in UTC to avoid timezone issues
 * 
 * @param {string|Date} dateInput - The contract date to check
 * @param {string} type - 'start' or 'end' (affects styling logic)
 * @returns {string} CSS class string
 */
export function getContractDateClass(dateInput, type = 'end') {
  if (!dateInput) return 'text-gray-600';
  
  // Parse date (handles both string and Date objects)
  const contractDate = dateInput instanceof Date ? dateInput : new Date(dateInput);
  
  // Check if date is valid
  if (isNaN(contractDate.getTime())) {
    return 'text-gray-600';
  }
  
  // Get today's date in UTC (normalized to midnight for comparison)
  const today = new Date();
  const todayUTC = new Date(Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
    0, 0, 0, 0
  ));
  
  // Get contract date in UTC (normalized to midnight)
  const contractUTC = new Date(Date.UTC(
    contractDate.getUTCFullYear(),
    contractDate.getUTCMonth(),
    contractDate.getUTCDate(),
    0, 0, 0, 0
  ));
  
  // Calculate difference in days
  const diffDays = Math.ceil((contractUTC - todayUTC) / (1000 * 60 * 60 * 24));
  
  // For end dates: show warning colors if expiring soon or expired
  if (type === 'end') {
    if (diffDays < 0) return 'text-red-600 font-medium'; // Expired
    if (diffDays <= 30) return 'text-orange-600 font-medium'; // Expiring within 30 days
    return 'text-gray-600'; // Normal
  }
  
  // For start dates: show different styling (future dates in blue, past in gray)
  if (type === 'start') {
    if (diffDays < 0) return 'text-gray-600'; // Past date
    if (diffDays <= 7) return 'text-blue-600 font-medium'; // Starting within 7 days
    return 'text-gray-600'; // Normal
  }
  
  return 'text-gray-600';
}

